// BILLING-04 credit statement (IA §2.1 under /{org}/settings/billing): balance, held and available
// amounts, the full ledger newest first (cursor pages) and the truthful top-up state.
import { t } from "@ady/i18n";
import { BusyRegion, SkeletonText } from "@ady/ui";
import { AppLink } from "../shell/AppLink";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import { LoadError, SettingsPage, SettingsSection } from "../settings/SettingsPage";
import { useLedger, useWallet } from "./api";
import { BILLING_TID, CreditSummary, LedgerTable } from "./components";

export function WalletPage() {
  const { membership } = useShell();
  const orgId = membership.org.id;
  const canRead = can(membership, ["billing.read"]);
  const wallet = useWallet(orgId, canRead);
  const ledger = useLedger(orgId, canRead);
  const entries = ledger.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <SettingsPage
      title={t("wallet.statement.title")}
      lead={t("wallet.statement.lead", { org: membership.org.name })}
      anyOf={["billing.read"]}
      testId={BILLING_TID.walletPage}
      canonical="BILLING-001"
      actions={
        <AppLink href={`/${membership.org.slug}/settings/billing`}>
          {t("wallet.statement.back")}
        </AppLink>
      }
    >
      <SettingsSection title={t("wallet.credit.title")} id="wallet-credit">
        {wallet.isError ? (
          <LoadError error={wallet.error} onRetry={() => void wallet.refetch()} />
        ) : (
          <BusyRegion busy={wallet.isPending} skeleton={<SkeletonText lines={2} />}>
            {wallet.data ? <CreditSummary view={wallet.data} /> : null}
          </BusyRegion>
        )}
      </SettingsSection>
      <SettingsSection title={t("wallet.statement.title")} id="wallet-statement">
        <LedgerTable
          entries={entries}
          state={ledger.isError ? "error" : ledger.isPending ? "loading" : "ready"}
          onRetry={() => void ledger.refetch()}
          hasMore={ledger.hasNextPage}
          loadingMore={ledger.isFetchingNextPage}
          onMore={() => void ledger.fetchNextPage()}
        />
      </SettingsSection>
    </SettingsPage>
  );
}
